import { useRef, type ReactNode } from "react";
import { cn } from "@/lib/utils/cn";

export interface TabItem {
  value: string;
  label: string;
  icon?: ReactNode;
}

export interface TabsProps {
  /** Shared id namespace - pass the same value to sibling TabPanel(s). */
  id: string;
  items: TabItem[];
  value: string;
  onChange: (value: string) => void;
  label: string;
}

/** Accessible tablist with roving tabindex and arrow-key navigation (WAI-ARIA tabs pattern). */
export function Tabs({ id: baseId, items, value, onChange, label }: TabsProps) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  function focusByIndex(index: number) {
    const item = items[((index % items.length) + items.length) % items.length];
    if (!item) return;
    onChange(item.value);
    refs.current[item.value]?.focus();
  }

  function handleKeyDown(event: React.KeyboardEvent, currentIndex: number) {
    switch (event.key) {
      case "ArrowRight":
        event.preventDefault();
        focusByIndex(currentIndex + 1);
        break;
      case "ArrowLeft":
        event.preventDefault();
        focusByIndex(currentIndex - 1);
        break;
      case "Home":
        event.preventDefault();
        focusByIndex(0);
        break;
      case "End":
        event.preventDefault();
        focusByIndex(items.length - 1);
        break;
    }
  }

  return (
    <div
      role="tablist"
      aria-label={label}
      className="scrollbar-thin flex gap-1 overflow-x-auto border-b border-ink/10"
    >
      {items.map((item, index) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            ref={(el) => {
              refs.current[item.value] = el;
            }}
            role="tab"
            id={`${baseId}-tab-${item.value}`}
            aria-selected={selected}
            aria-controls={`${baseId}-panel-${item.value}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(item.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={cn(
              "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors",
              selected
                ? "border-brand-700 text-brand-800"
                : "border-transparent text-ink-muted hover:text-ink",
            )}
          >
            {item.icon}
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({
  value,
  activeValue,
  baseId,
  children,
}: {
  value: string;
  activeValue: string;
  baseId: string;
  children: ReactNode;
}) {
  if (value !== activeValue) return null;
  return (
    <div
      role="tabpanel"
      id={`${baseId}-panel-${value}`}
      aria-labelledby={`${baseId}-tab-${value}`}
      tabIndex={0}
    >
      {children}
    </div>
  );
}
